// EmailAnalyticsPanel.js

/*
get Date Filters
show click rate,open rate , by stage 

*/
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, ToggleButtonGroup, ToggleButton, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Chip, Grid, Tabs, Tab, Card, CardContent,
  useTheme, alpha
} from '@mui/material';
import { Group, MailOutline, BarChart, FilterList, Search } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const statusStages = [
  "Initial Outreach Pending",
  "Followup Pending",
  "Closing Outreach Pending",
  "Closing Outreach",
  "Response Received",
  "Rejected"
];

const statusColors = {
  "Initial Outreach Pending": "info",
  "Followup Pending": "warning",
  "Closing Outreach Pending": "secondary",
  "Closing Outreach": "success",
  "Response Received": "primary",
  "Rejected": "error"
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  },
}));

const EmailAnalyticsPanel = ({ decisionMakers = [], emails = [], companies = [] }) => {
  const theme = useTheme();
  const [displayMode, setDisplayMode] = useState("count");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [tab, setTab] = useState(0);
  const [trackType, setTrackType] = useState("unique");

  const mapDMStatus = (status) => {
    switch (status) {
      case "Initial Outreach": return "Followup Pending";
      case "Follow up": return "Closing Outreach Pending";
      default: return status;
    }
  };

  const getEmailStatsByDM = useMemo(() => {
    const stats = {};
    emails.forEach(email => {
      const to = email.to;
      if (!stats[to]) stats[to] = { sent: 0, opened: 0, clicked: 0, replied: 0 };
      stats[to].sent++;
      const logged = new Set();
      email.logs?.forEach(log => {
        if (log.event === "Open" && (trackType === 'all' || !logged.has('opened'))) {
          stats[to].opened++;
          logged.add('opened');
        }
        if (log.event === "Link Clicked" && (trackType === 'all' || !logged.has('clicked'))) {
          stats[to].clicked++;
          logged.add('clicked');
        }
        if (log.event === "Replied") stats[to].replied++;
      });
    });
    return stats;
  }, [emails, trackType]);

  const companySummary = useMemo(() => {
    const summary = {};
    decisionMakers.forEach(dm => {
      const companyId = dm.TargetCompanyId;
      const email = dm.WorkEmail;
      if (!summary[companyId]) {
        const company = companies.find(c => c.Organization === companyId);
        summary[companyId] = {
          name: company?.Name || "Unknown",
          totalDMs: 0,
          statusMap: {},
          emailStats: { sent: 0, opened: 0, clicked: 0, replied: 0 },
          responded: 0
        };
        statusStages.forEach(stage => summary[companyId].statusMap[stage] = 0);
      }
      summary[companyId].totalDMs++;
      summary[companyId].statusMap[mapDMStatus(dm.status)]++;
      if (["Response Received", "Positive Outcome", "Negative Outcome"].includes(dm.status)) {
        summary[companyId].responded++;
      }
      if (getEmailStatsByDM[email]) {
        const s = getEmailStatsByDM[email];
        summary[companyId].emailStats.sent += s.sent;
        summary[companyId].emailStats.opened += s.opened;
        summary[companyId].emailStats.clicked += s.clicked;
        summary[companyId].emailStats.replied += s.replied;
      }
    });
    return Object.values(summary);
  }, [decisionMakers, companies, getEmailStatsByDM]);

  const totalStats = useMemo(() => {
    //console.log("totalStats", companySummary);
    const flat = companySummary;
    const allDMs = flat.reduce((acc, row) => acc + row.totalDMs - (row.statusMap["Rejected"] || 0) - (row.statusMap["Initial Outreach Pending"]|| 0), 0);
    const totalSent = flat.reduce((acc, row) => acc + row.emailStats.sent, 0);
    const totalOpen = flat.reduce((acc, row) => acc + row.emailStats.opened, 0);
    const totalClick = flat.reduce((acc, row) => acc + row.emailStats.clicked, 0);
    const totalReply = flat.reduce((acc, row) => acc + row.emailStats.replied, 0);
    const responseReceived = flat.reduce((acc, row) => acc + row.responded, 0);
    const connectedCompanies = flat.filter(row => row.responded > 0).length;
    return {
      companies: flat.length,
      dms: allDMs,
      sent: totalSent,
      open: totalOpen,
      click: totalClick,
      reply: totalReply,
      responseReceived,
      connectedCompanies
    };
  }, [companySummary]);

  return (
    <Box sx={{ p: 3 }}>

      <Tabs 
        value={tab} 
        onChange={(e, v) => setTab(v)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 }
        }}
      >
        <Tab label="Overview" icon={<BarChart fontSize="small" />} />
        <Tab label="DM Pipeline" icon={<Group fontSize="small" />} />
        <Tab label="Email Insights" icon={<MailOutline fontSize="small" />} />
      </Tabs>

      {/* Overview Tab */}
      <Box mt={4} display={tab !== 0 ? 'none' : 'block'}>
        
        <Grid container spacing={3}>
          {[            
            { label: 'Companies', value: totalStats.companies, icon: <Group /> },
            { label: 'Decision Makers', value: totalStats.dms, icon: <Group /> },
            { label: 'Emails Sent', value: `${totalStats.sent}/${totalStats.dms}`, icon: <MailOutline /> },
            { label: 'Opened Rate', value: `${(totalStats.open / totalStats.sent * 100 || 0).toFixed(1)}%`, icon: <BarChart /> },
            { label: 'Clicked Rate', value: `${(totalStats.click / totalStats.sent * 100 || 0).toFixed(1)}%`, icon: <BarChart /> },
            { label: 'Replies', value: totalStats.reply, icon: <MailOutline /> },
            { label: 'Responses Received', value: totalStats.responseReceived, icon: <Group /> },
            { label: 'Connected Companies', value: totalStats.connectedCompanies, icon: <Group /> },
          ].map((metric, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <MetricCard>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ 
                    bgcolor: theme.palette.primary.light, 
                    p: 1.5, 
                    borderRadius: '50%',
                    color: theme.palette.primary.contrastText
                  }}>
                    {metric.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">{metric.label}</Typography>
                    <Typography variant="h6" fontWeight={600}>{metric.value}</Typography>
                  </Box>
                </CardContent>
              </MetricCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* DM Pipeline Tab */}
      <Box mt={4} display={tab !== 1 ? 'none' : 'block'}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Group color="primary" /> Company Status Distribution
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search company..."
            InputProps={{ startAdornment: <Search fontSize="small" /> }}
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
          />
        </Box>
        
        <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>DMs</TableCell>
                {statusStages.map(stage => (
                  <TableCell key={stage} align="center" sx={{ fontWeight: 600 }}>
                    <Chip 
                      label={stage} 
                      size="small" 
                      color={statusColors[stage]}
                      sx={{ textTransform: 'none' }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {companySummary.filter(c => c.name.toLowerCase().includes(searchCompany.toLowerCase())).map((row, idx) => (
                <StyledTableRow key={idx}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="right">{row.totalDMs}</TableCell>
                  {statusStages.map(stage => (
                    <TableCell key={stage} align="center">
                      <Typography variant="body2" fontWeight={500}>
                        {displayMode === 'count' ? row.statusMap[stage] : `${((row.statusMap[stage] / row.totalDMs) * 100).toFixed(1)}%`}
                      </Typography>
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Email Insights Tab */}
      <Box mt={4} display={tab !== 2 ? 'none' : 'block'}>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          flexWrap: 'wrap',
          '& > *': { minWidth: 120 }
        }}>
          <ToggleButtonGroup 
            color="primary"
            value={displayMode} 
            exclusive 
            onChange={(e, val) => setDisplayMode(val)} 
            size="small"
          >
            <ToggleButton value="count">Count</ToggleButton>
            <ToggleButton value="percent">Percentage</ToggleButton>
          </ToggleButtonGroup>
          
          <ToggleButtonGroup 
            color="primary"
            value={trackType} 
            exclusive 
            onChange={(e, val) => setTrackType(val)} 
            size="small"
          >
            <ToggleButton value="unique">Unique</ToggleButton>
            <ToggleButton value="all">Total</ToggleButton>
          </ToggleButtonGroup>
          
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter companies..."
            InputProps={{ startAdornment: <Search fontSize="small" /> }}
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
          />
        </Box>

        <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Sent</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Opened</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Clicked</TableCell>
                {totalStats.reply > 0 && <TableCell align="right" sx={{ fontWeight: 600 }}>Replied</TableCell>}
                <TableCell align="right" sx={{ fontWeight: 600 }}>Open Rate</TableCell>
                {totalStats.reply > 0 && <TableCell align="right" sx={{ fontWeight: 600 }}>Reply Rate</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {companySummary.filter(c => c.name.toLowerCase().includes(searchCompany.toLowerCase())).map((row, idx) => {
                const { sent, opened, clicked, replied } = row.emailStats;
                const openRate = sent > 0 ? (opened / sent) * 100 : 0;
                const replyRate = sent > 0 ? (replied / sent) * 100 : 0;

                return (
                  <StyledTableRow key={idx}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{sent}</TableCell>
                    <TableCell align="right">{opened}</TableCell>
                    <TableCell align="right">{clicked}</TableCell>
                    {totalStats.reply > 0 && <TableCell align="right">{replied}</TableCell>}
                    <TableCell align="right">
                      <Box sx={{ 
                        color: openRate >= 50 ? theme.palette.success.main : theme.palette.error.main,
                        fontWeight: 500
                      }}>
                        {sent > 0 ? `${openRate.toFixed(1)}%` : '-'}
                      </Box>
                    </TableCell>
                    {totalStats.reply > 0 && (
                      <TableCell align="right">
                        <Box sx={{ 
                          color: replyRate >= 20 ? theme.palette.success.main : theme.palette.error.main,
                          fontWeight: 500
                        }}>
                          {sent > 0 ? `${replyRate.toFixed(1)}%` : '-'}
                        </Box>
                      </TableCell>
                    )}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default EmailAnalyticsPanel;